import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@frontegg/react';
import { registerTokenGetter } from '../lib/auth-token';

const DEV_AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
const DEV_STUB_USER_ID = "dev-user-123";

interface UserContextType {
  userId: string | null;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  // Register synchronously during render — not in useEffect — so the token is
  // always available before any child component effects fire (React runs child
  // effects before parent effects, which caused a race on the terms-status call).
  registerTokenGetter(() => user?.accessToken ?? null);

  const userId = DEV_AUTH_BYPASS ? DEV_STUB_USER_ID : (user?.id || null);

  return (
    <UserContext.Provider value={{ userId }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  // In preview-token sessions there is no UserProvider mounted (no Frontegg),
  // so fall back to a null userId instead of throwing. Components that need
  // a real user must guard on the value.
  if (context === undefined) {
    return { userId: null };
  }
  return context;
};